/**
 * Shop by machine: narrows a collection to the parts that fit the shopper's vehicle.
 *
 * How the pieces fit together
 * ---------------------------
 * The narrowing itself is a native Shopify filter on the product metafield fitment.machine,
 * whose values are "MAKE MODEL" strings. That metafield deliberately carries no year: Shopify
 * caps a list metafield at 128 entries, and keying by make+model+year came to 2.2 million keys.
 *
 * So the year is resolved on the page instead. Liquid knows which machine is filtered (it can
 * read the active filter), so for every card it scans that product's real fitment data and
 * writes the matching year ranges into data-fit-years. This file compares the shopper's year
 * against those ranges and labels the card.
 *
 * The labels never overstate. A card can say it fits, or it can say which years it does fit and
 * ask the shopper to check - it never claims a part does NOT fit, because a gap in the data is
 * not the same as a known incompatibility.
 *
 * Parts that fit more than 128 machines carry the single value UNIVERSAL instead of a truncated
 * list. They are excluded by default - a machine's exact matches are what a shopper came for -
 * and can be folded in with the checkbox.
 */
(function () {
  "use strict";

  /**
   * The words a spelling carries beyond the machine, and the year ranges, come from
   * fitment-match.js - the same reading the product finder and the results page use, so the
   * badge on a collection card cannot disagree with the badge on the page it leads to.
   */
  var F = window.GHSFitment;

  var FILTER_KEY = "filter.p.m.fitment.machine";
  var YEAR_PARAM = "fit_year";
  /** The machine the shopper picked, kept beside the spellings it expanded into. */
  var MACHINE_PARAM = "fit_machine";
  var UNIVERSAL = "UNIVERSAL";
  var GARAGE_KEY = "ghs:garage";
  /** Whether the shopper last left the box open. Their choice, kept across pages. */
  var OPEN_KEY = "ghs:finder-open";
  /** Which tab they last used, machine or tire. Kept for the same reason. */
  var MODE_KEY = "ghs:finder-mode";
  /** The tire size they last searched, kept the way the garage keeps their machine. */
  var TIRE_KEY = "ghs:tire";
  var PRODUCT_CARD = "product-card, .card-product, [data-product-id]";
  var PROBE_TIMEOUT = 6000;

  /* ---------------------------------------------------------------- garage */

  function readGarage() {
    try {
      var raw = window.localStorage.getItem(GARAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeGarage(machine) {
    try {
      window.localStorage.setItem(GARAGE_KEY, JSON.stringify(machine));
    } catch (e) {
      /* private browsing; the selector still works for this page */
    }
  }

  function clearGarage() {
    try {
      window.localStorage.removeItem(GARAGE_KEY);
    } catch (e) {}
    announceGarage();
  }

  /**
   * Tells the rest of the page the saved machine changed. The fit box on a product page listens,
   * so it can answer for the new machine without a reload. localStorage fires no event in the tab
   * that wrote it, hence this one.
   */
  function announceGarage() {
    try {
      document.dispatchEvent(new CustomEvent("ghs:garage-change"));
    } catch (e) {}
  }

  function readOpen() {
    try {
      var raw = window.localStorage.getItem(OPEN_KEY);
      return raw === null ? null : raw === "1";
    } catch (e) {
      return null;
    }
  }

  function writeOpen(open) {
    try {
      window.localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch (e) {}
  }

  function readTire() {
    try {
      var raw = window.localStorage.getItem(TIRE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeTire(size) {
    try {
      window.localStorage.setItem(TIRE_KEY, JSON.stringify(size));
    } catch (e) {}
  }

  function readMode() {
    try {
      return window.localStorage.getItem(MODE_KEY);
    } catch (e) {
      return null;
    }
  }

  function writeMode(mode) {
    try {
      window.localStorage.setItem(MODE_KEY, mode);
    } catch (e) {}
  }

  /* ------------------------------------------------------------ fit badges */

  /**
   * Parses "2018-2024,2015" into ranges. "many" means the product fits more machines than
   * Liquid was willing to scan, so nothing can be concluded about the year.
   */
  function parseRanges(value) {
    if (!value || value === "many") return null;
    return value
      .split(",")
      .map(function (part) {
        var bits = part.split("-");
        var start = parseInt(bits[0], 10);
        var end = bits.length > 1 ? parseInt(bits[1], 10) : start;
        return isNaN(start) ? null : [start, end];
      })
      .filter(Boolean);
  }

  /**
   * The words every filtered spelling shares - "POLARIS SPORTSMAN 850" out of SPORTSMAN 850 SP,
   * 850 PREMIUM and the rest. Subtracting it from a spelling leaves the trim, which is the part
   * the shopper has to check against their own machine.
   */
  function commonPrefix(values) {
    if (!values.length) return "";
    var parts = values[0].split(" ");
    values.forEach(function (value) {
      var words = value.split(" ");
      var i = 0;
      while (i < parts.length && i < words.length && parts[i] === words[i]) i++;
      parts = parts.slice(0, i);
    });
    return parts.join(" ");
  }

  /**
   * Names the trims a part is established for, when the shopper filtered on a whole machine and
   * the part covers only some of it.
   *
   * Showing it at all is the decision here: the alternative was hiding every part not filed
   * under all trims, which hides real parts from the people who need them. So it is shown and
   * labelled, and the label names the trim rather than muttering "may not fit" - a shopper can
   * check a name against their own machine, and cannot check a vague warning against anything.
   */
  function trimNote(card, machine) {
    var raw = card.getAttribute("data-fit-machines");
    if (!raw || !machine) return null;
    var names = raw.split("|").map(function (label) {
      return F.readableTrim(F.trimAgainst(machine, label));
    });
    /**
     * Two of Gamma's listings are the machine itself rather than a trim: the plain name, and the
     * catch-all it writes as "(ALL)". A part filed under either is filed against the whole
     * machine and gets no warning - measured on a 2019 Sportsman 850, warning on those put a
     * "check your trim" on 40 of 185 parts that did not need one, and a badge that cries wolf is
     * a badge shoppers stop reading.
     */
    var whole = names.some(function (name) {
      // Both spellings of the catch-all: the catalogue writes "(ALL)" and "ALL", and the brackets
      // have already been taken off by the time this runs.
      return name === "" || name === "ALL";
    });
    if (whole) return null;
    var shown = names.filter(Boolean);
    if (!shown.length) return null;
    var text = shown.slice(0, 3).join(", ");
    if (shown.length > 3) text += " and " + (shown.length - 3) + " more";
    return "Fits " + text + " only - check your trim";
  }

  /**
   * The make on its own, for the badge. The filter value is "MAKE MODEL" with no separator and
   * the machine index is not loaded on a collection page, so the make is taken from the machine
   * the shopper picked - which is where the filter came from in the first place. A make with a
   * space in it, ARCTIC CAT, is why the first word will not do.
   */
  function makeOf(machine) {
    var garage = readGarage();
    if (!garage || !garage.make || !machine) return "";
    return machine.indexOf(garage.make) === 0 ? garage.make : "";
  }

  /* ------------------------------------------------------------------------- words */

  /**
   * The words the snippet handed over, read once for the page and remembered.
   *
   * Module level rather than on the controller because the fitment badges are plain functions:
   * a card can say "Convient a votre POLARIS" without a selector being on the page at all.
   * An empty object is a fine answer - every caller carries the English it was written with.
   */
  /**
   * The words arrive HTML-escaped, because Shopify escapes whatever the `t` filter returns and
   * the JSON block is written straight out of it. Every one of them is handed to `textContent`
   * or to `placeholder`, which show a "&#39;" as those five characters rather than as the
   * apostrophe the translator typed, so they are turned back into text once, here, on the way in.
   * "&amp;" is decoded last, so a translation that really does say "&#39;" survives as itself.
   */
  function decodeEntities(text) {
    if (text.indexOf("&") === -1) return text;
    return text
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&");
  }

  function decodeStrings(value) {
    if (typeof value === "string") return decodeEntities(value);
    if (value && typeof value === "object") {
      for (var key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          value[key] = decodeStrings(value[key]);
        }
      }
    }
    return value;
  }

  var STRINGS = null;
  function strings() {
    if (STRINGS) return STRINGS;
    STRINGS = {};
    var el = document.querySelector("[data-sbm-strings]");
    if (el) {
      try {
        STRINGS = decodeStrings(JSON.parse(el.textContent) || {});
      } catch (e) {
        /* a broken block is not a reason to render nothing */
      }
    }
    return STRINGS;
  }

  /**
   * One of those words, with the {{ name }} slots filled in from `vars`. Numbers are written
   * the way the shopper's own locale writes them, so 4,962 and 4 962 both come out right.
   */
  function say(key, fallback, vars) {
    var text = strings()[key] || fallback;
    if (!vars) return text;
    return String(text).replace(/\{\{\s*(\w+)\s*\}\}/g, function (whole, name) {
      if (!(name in vars)) return whole;
      var v = vars[name];
      return typeof v === "number" ? v.toLocaleString() : String(v);
    });
  }

  function labelFor(card, year, machine, make) {
    var raw = card.getAttribute("data-fit-years");
    if (raw === null) return null;

    if (raw === "universal") {
      return { state: "similar", text: say("fits_many", "Fits many machines - check fitment") };
    }
    var trim = trimNote(card, machine);
    var ranges = parseRanges(raw);
    // No year data at all: outdoor equipment is not sold by model year, so the machine
    // matching is all the confirmation there is, and it is real confirmation.
    if (raw === "") {
      return trim ? { state: "family", text: trim } : { state: "exact", text: fitsLabel(make) };
    }
    if (!ranges) return { state: "similar", text: say("check_fitment", "Check fitment") };
    // Ranges arrive per matched trim and can overlap; merged so the card shows 2015-2019 rather
    // than the same years twice.
    ranges = F.mergeRanges(ranges);
    if (!year) {
      return trim
        ? { state: "family", text: trim + " (" + F.formatRanges(ranges) + ")" }
        : { state: "exact", text: say("fits_years", "Fits {{ years }}", { years: F.formatRanges(ranges) }) };
    }

    for (var i = 0; i < ranges.length; i++) {
      if (year >= ranges[i][0] && year <= ranges[i][1]) {
        // The year is settled, so the trim is the only question left. A part established for
        // every trim of the machine has no question at all.
        return trim ? { state: "family", text: trim } : { state: "exact", text: fitsLabel(make) };
      }
    }
    // The part fits this model, just not this year as far as the data goes. Say which years
    // it does fit rather than asserting it will not work.
    return {
      state: "family",
      text: say("fits_years_check_year", "Fits {{ years }} - check your year", {
        years: F.formatRanges(ranges)
      })
    };
  }

  /** "Fits your POLARIS", or the machine in general where the make could not be worked out. */
  function fitsLabel(make) {
    return make
      ? say("fits_your_make", "Fits your {{ make }}", { make: make })
      : say("fits_your_machine", "Fits your machine");
  }

  function paintBadges() {
    var params = new URLSearchParams(window.location.search);
    var year = parseInt(params.get(YEAR_PARAM) || "", 10) || null;
    var cards = document.querySelectorAll("[data-fit-years]");
    /**
     * The machine they picked, written out when the filter was built. Falls back to the words its
     * spellings share, which is what a link made before this existed carries - and all that can
     * be worked out from the spellings alone.
     */
    var machine =
      params.get(MACHINE_PARAM) ||
      commonPrefix(
        params.getAll(FILTER_KEY).filter(function (v) {
          return v !== UNIVERSAL;
        })
      );

    var make = makeOf(machine);

    cards.forEach(function (card) {
      var label = labelFor(card, year, machine, make);
      if (!label) return;
      var slot = card.querySelector("[data-fit-badge]");
      if (!slot) return;
      slot.className = "sbm-fit-badge sbm-fit-" + label.state;
      slot.textContent = label.text;
      slot.hidden = false;
    });
  }

  /* --------------------------------------------------------------- selector */

  /**
   * The vehicle types the Type dropdown offers.
   *
   * Gamma and Kimpex between them publish these seven, and they are the machines this selector
   * was built for. The catalogue also carries outdoor types that come from Husqvarna's own site
   * - Chain Saws, Ride-On Lawn Mowers, Snow Throwers and a dozen more - and those are held back
   * for now. They are not removed from the data: leaving Type on "All types" still reaches them,
   * and a lawn tractor is still found by its make and model.
   */
  var OFFERED_TYPES = [
    "ATV",
    "OFF-ROAD MOTORCYCLE",
    "PWC",
    "SCOOTER",
    "SNOWMOBILE",
    "STREET MOTORCYCLE",
    "UTV/SXS"
  ];

  /**
   * "All types", as a choice of its own. The Type box's empty value now means "not answered
   * yet", which keeps the Year box shut, so skipping the type has to be something the shopper
   * picks. Never saved or filtered on: it means no type at all.
   */
  var ALL_TYPES = "*";

  /**
   * A type-to-search box: a text field, a list of matches under it, and a hidden <select> that
   * stays the single source of truth for what was actually chosen.
   *
   * Year, Make and Model all use one. The model list was always this way - Arctic Cat alone
   * publishes 4,962 of them - and the make and year lists are the same shape of problem, just
   * smaller: a hundred-odd makes and sixty-odd years are both more than a shopper wants to
   * scroll when they already know the word they are looking for.
   *
   * Typing unsettles the choice, so the button cannot submit something the box no longer shows.
   * Leaving the box puts the settled choice back, so a half-typed word never sits there looking
   * like an answer - and clearing the box and leaving it means "no choice", which is how the
   * year goes back to any year.
   */
  function Combo(field, opts) {
    this.opts = opts || {};
    this.items = [];
    this.active = -1;
    this.settled = "";
    this.loaded = false;
    this.field = field;
    if (!field) return;
    this.input = field.querySelector("input[type='text']");
    this.list = field.querySelector(".sbm-combo-list");
    this.select = field.querySelector("select");
    if (this.input) this.placeholder = this.input.placeholder;
    this.bind();
  }

  Combo.prototype.ok = function () {
    return Boolean(this.input && this.list && this.select);
  };

  Combo.prototype.value = function () {
    return this.ok() ? this.select.value : "";
  };

  /** Writes the choice into the hidden select, which is what the rest of the file reads. */
  Combo.prototype.settle = function (value) {
    if (!this.ok()) return;
    this.select.innerHTML = "";
    var opt = document.createElement("option");
    opt.value = value;
    opt.textContent = value;
    this.select.appendChild(opt);
    this.select.value = value;
  };

  /** Sets both halves without telling anyone: used when restoring a remembered machine. */
  Combo.prototype.set = function (value) {
    this.settled = value;
    if (!this.ok()) return;
    this.settle(value);
    this.input.value = value;
  };

  Combo.prototype.clear = function () {
    this.set("");
  };

  Combo.prototype.setItems = function (items, placeholder) {
    if (!this.ok()) return;
    this.items = items || [];
    this.loaded = true;
    this.input.disabled = this.items.length === 0;
    if (placeholder) {
      this.placeholder = placeholder;
      this.input.placeholder = placeholder;
    }
    this.close();
  };

  /**
   * Opens the box only once the step before it is answered, and until then says which step
   * that is. An open box with nothing to offer stays shut too, and says why in its own words.
   */
  Combo.prototype.gate = function (open, waiting) {
    if (!this.ok()) return;
    this.input.disabled = !open || (this.loaded && this.items.length === 0);
    this.input.placeholder = open ? this.placeholder || "" : waiting;
    if (!open) this.close();
  };

  Combo.prototype.close = function () {
    if (!this.ok()) return;
    this.list.hidden = true;
    this.list.innerHTML = "";
    this.active = -1;
    this.input.setAttribute("aria-expanded", "false");
  };

  /**
   * Ranks matches so the shopper's own words win: an entry starting with what they typed comes
   * before one that merely contains it. Capped, because past that the answer is "keep typing".
   */
  Combo.prototype.matches = function (typed) {
    if (this.opts.matches) return this.opts.matches(typed);
    var cap = typed ? 25 : 100;
    var starts = [];
    var contains = [];
    for (var i = 0; i < this.items.length; i++) {
      var item = String(this.items[i]);
      if (!typed) starts.push(item);
      else if (item.indexOf(typed) === 0) starts.push(item);
      else if (item.indexOf(typed) !== -1) contains.push(item);
      if (starts.length >= cap) break;
    }
    return starts.concat(contains).slice(0, cap);
  };

  Combo.prototype.open = function () {
    if (!this.ok()) return;
    var typed = this.input.value.trim().toUpperCase();
    var matches = this.matches(typed);
    this.list.innerHTML = "";
    if (!matches.length) {
      this.close();
      return;
    }
    var self = this;
    matches.forEach(function (item, i) {
      var li = document.createElement("li");
      li.setAttribute("role", "option");
      li.setAttribute("id", (self.input.id || "sbm") + "-opt-" + i);
      li.textContent = item;
      li.addEventListener("mousedown", function (e) {
        e.preventDefault();
        self.choose(item);
      });
      self.list.appendChild(li);
    });
    this.list.hidden = false;
    this.active = -1;
    this.input.setAttribute("aria-expanded", "true");
  };

  Combo.prototype.highlight = function (delta) {
    var options = this.list.querySelectorAll("li");
    if (!options.length) return;
    this.active = (this.active + delta + options.length) % options.length;
    var active = this.active;
    options.forEach(function (li, i) {
      li.classList.toggle("is-active", i === active);
    });
    options[this.active].scrollIntoView({ block: "nearest" });
  };

  Combo.prototype.choose = function (value) {
    if (!this.ok()) return;
    var before = this.settled || "";
    this.set(value);
    this.close();
    if (value !== before) {
      if (this.opts.onChange) this.opts.onChange(value);
    } else if (this.opts.onSettle) {
      this.opts.onSettle(value);
    }
  };

  Combo.prototype.bind = function () {
    if (!this.ok()) return;
    var self = this;

    this.input.addEventListener("input", function () {
      // Typing unsettles the choice, so the button cannot submit a stale one.
      if (self.select.value) self.settle("");
      if (self.opts.onType) self.opts.onType();
      self.open();
    });
    this.input.addEventListener("focus", function () {
      self.open();
    });
    /**
     * Leaving the box settles it one way or the other: an empty box means no choice, and a
     * half-typed word goes back to whatever was last chosen. Either way the box and the hidden
     * select say the same thing, which is what the rest of the file relies on.
     */
    this.input.addEventListener("blur", function () {
      window.setTimeout(function () {
        self.close();
        var typed = self.input.value.trim();
        if (!typed) {
          if (self.settled) {
            self.set("");
            if (self.opts.onChange) self.opts.onChange("");
          }
          return;
        }
        if (self.select.value) return;
        self.set(self.settled || "");
        if (self.opts.onType) self.opts.onType();
      }, 120);
    });
    this.input.addEventListener("keydown", function (event) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (self.list.hidden) self.open();
        else self.highlight(1);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        self.highlight(-1);
      } else if (event.key === "Enter") {
        var active = self.list.querySelector("li.is-active");
        if (active) {
          event.preventDefault();
          self.choose(active.textContent);
        }
      } else if (event.key === "Escape") {
        self.close();
      }
    });
  };

  function SelectorController(root) {
    this.root = root;
    this.typeEl = root.querySelector("[data-sbm-type]");
    this.rawIndex = null;
    this.submitEl = root.querySelector("[data-sbm-submit]");
    this.clearEl = root.querySelector("[data-sbm-clear]");
    this.resetEl = root.querySelector("[data-sbm-reset]");
    this.searchLinkEl = root.querySelector("[data-sbm-search-link]");
    this.offerShown = false;
    this.universalEl = root.querySelector("[data-sbm-universal]");
    this.statusEl = root.querySelector("[data-sbm-status]");
    this.bodyEl = root.querySelector("[data-sbm-body]");
    this.toggleEl = root.querySelector("[data-sbm-toggle]");
    this.chosenEl = root.querySelector("[data-sbm-chosen]");
    this.index = null;
    this.loading = null;
    this.scopeLabels = null;
    this.scopeCounts = null;
    this.scope = null;
    this.makeScopeLabels = null;
    this.typeScope = null;
    this.makeScopeCounts = null;
    this.makeScope = null;
    this.models = [];
    this.visibleModels = [];
    this.trimOf = {};

    var self = this;

    /**
     * Type, then year, then make, then model.
     *
     * The model is the sharpest of the four and it is asked last on purpose: by the time a
     * shopper reaches it, the type, the year and the make have already taken the list from tens
     * of thousands of machines down to a few dozen, so the list they scroll is short and the
     * word they type finds it straight away.
     *
     * One at a time, in that order: each box opens only once the one before it is answered
     * (refreshSteps). Picking a make and then a year used to wipe the make, because a new year
     * refills the makes; asked in order, nothing is answered before what it depends on.
     *
     * The year can be skipped, but only by choosing "Any year" at the top of its list, which
     * narrows nothing. A machine the catalogue publishes no year for stays in the list whatever
     * year is chosen - outdoor equipment is not sold by model year, and a gap in the data is not
     * a machine that was never built.
     */
    this.yearCombo = new Combo(root.querySelector("[data-sbm-year-field]"), {
      onChange: function (value) {
        self.onYear();
        if (value) self.advance();
      },
      onType: function () {
        self.refreshSubmit();
      }
    });
    this.makeCombo = new Combo(root.querySelector("[data-sbm-make-field]"), {
      onChange: function (value) {
        self.onMake();
        if (value) self.advance();
      },
      onType: function () {
        self.refreshSubmit();
      }
    });
    this.modelCombo = new Combo(root.querySelector("[data-sbm-model-field]"), {
      matches: function (typed) {
        return self.modelMatches(typed);
      },
      onChange: function (value) {
        self.onModel();
        if (value) self.advance();
      },
      onSettle: function () {
        self.onModel();
      },
      onType: function () {
        self.refreshSubmit();
      }
    });

    // The hidden selects stay the single source of truth the rest of the file reads.
    this.yearEl = this.yearCombo.select;
    this.makeEl = this.makeCombo.select;
    this.modelEl = this.modelCombo.select;

    this.readScope();
    this.applyFold();
    this.showChosen();
    this.bind();
    this.refreshSteps();
    this.restore();
  }

  /**
   * Folds the box away, or opens it, and remembers which.
   *
   * Left folded it is one line - a title and the shopper's machine if they have one - instead of
   * a row of boxes standing in front of the products. Liquid has already decided the state this
   * page starts in; the shopper's own last choice wins over it, except where the box must stay
   * open (a page already narrowed to a machine, or the theme editor), which Liquid marks by
   * leaving off data-sbm-foldable.
   */
  SelectorController.prototype.applyFold = function () {
    if (!this.bodyEl || !this.toggleEl) return;
    this.foldable = this.bodyEl.hasAttribute("data-sbm-foldable");
    if (!this.foldable) {
      this.setOpen(true, false);
      return;
    }
    var remembered = readOpen();
    this.setOpen(remembered === null ? !this.bodyEl.hidden : remembered, false);
  };

  SelectorController.prototype.setOpen = function (open, remember) {
    if (!this.bodyEl || !this.toggleEl) return;
    this.bodyEl.hidden = !open;
    this.toggleEl.setAttribute("aria-expanded", open ? "true" : "false");
    this.root.classList.toggle("is-open", Boolean(open));
    // A fit box's "stay on this page" lasts only for the opening it asked for.
    if (!open) this.root.removeAttribute("data-sbm-stay");
    if (remember && this.foldable) writeOpen(open);
    // Opening is a clear sign the shopper is about to use it, so the index is fetched now if
    // hovering the heading has not already started it.
    if (open) this.load();
  };

  /**
   * The kind of machine this is, read from the catalogue rather than from the Type box.
   *
   * They are two different things and only one of them belongs on the label. The Type box is a
   * filter - left on "All types" it says nothing about the machine - while this is what the
   * catalogue files the machine under, so it is there to be shown whether the box was used or
   * not. A machine the catalogue publishes no type for gets no label rather than a guess.
   */
  SelectorController.prototype.typeOfMachine = function (make, model) {
    var types = this.rawIndex && this.rawIndex.types;
    if (!types || !make || !model) return "";
    var names = Object.keys(types);
    for (var i = 0; i < names.length; i++) {
      var type = names[i];
      if (!type) continue;
      var tree = types[type][make];
      if (!tree) continue;
      if (tree[model]) return type;
      // A plain machine name is not in the file - it stands for the spellings under it - so a
      // group's members are what settles the type for it.
      var members = this.groups && this.groups[make] && this.groups[make][model];
      if (members) {
        for (var j = 0; j < members.length; j++) {
          if (tree[members[j]]) return type;
        }
      }
    }
    return "";
  };

  /**
   * The machine the shopper has already picked, written beside the title so the folded box says
   * whose parts it is showing rather than sitting there anonymously. The kind of machine leads,
   * because "ATV" tells someone at a glance that this is the right box to be looking at.
   */
  /**
   * The line beside the title while the box is folded.
   *
   * One chip, two questions. Whichever tab they last used is the one that speaks: a shopper who
   * searched a tire size is told their size, and one who picked a machine is told their machine.
   * Showing both would need two chips on a line that is one line on purpose, and showing the
   * machine to somebody in the middle of buying tires answers a question they are not asking.
   */
  SelectorController.prototype.showChosen = function () {
    if (!this.chosenEl) return;

    if (readMode() === "tire") {
      var tire = readTire();
      if (tire && tire.label) {
        this.chosenEl.textContent =
          (tire.type === "ATV" ? say("tire_atv", "ATV") : say("tire_motorcycle", "Motorcycle")) +
          " · " +
          tire.label;
        this.chosenEl.hidden = false;
        return;
      }
    }

    var garage = readGarage();
    if (!garage || !garage.make || !garage.model) {
      this.chosenEl.hidden = true;
      this.chosenEl.textContent = "";
      return;
    }
    var kind = garage.machineType || this.typeOfMachine(garage.make, garage.model);
    this.chosenEl.textContent =
      (kind ? kind + " · " : "") + (garage.year ? garage.year + " " : "") + garage.make + " " + garage.model;
    this.chosenEl.hidden = false;
  };

  /**
   * The machines this collection actually stocks parts for, written out by Liquid from the
   * storefront facet - present only when that list was short enough to be complete. Absent
   * means "offer the whole catalogue", which is what the selector did before.
   */
  SelectorController.prototype.readScope = function () {
    var machines = this.readJson("[data-sbm-collection-machines]");
    if (machines) {
      this.scopeLabels = machines.labels;
      this.scopeCounts = machines.counts || null;
    }
    // Written out only where a make filter exists in Search & Discovery. A collection holds
    // only a handful of makes, so unlike the machine facet this list is complete: ATV/UTV
    // Brake Pads returns 22, where its truncated machine facet only ever revealed 7 of them.
    var makes = this.readJson("[data-sbm-collection-makes]");
    if (makes) {
      this.makeScopeLabels = makes.labels;
      this.makeScopeCounts = makes.counts || null;
    }
    /**
     * The vehicle types this collection holds parts for, and the only facet that is always
     * complete - the catalogue has 26 types and a collection carries two or three. It is what
     * keeps 2,930 Polaris snowmobiles out of a brake pad collection for quads.
     *
     * A value at zero is dropped: Shopify keeps returning a facet value once another filter has
     * excluded it, and a type with nothing left in it is not a type this collection has.
     */
    var types = this.readJson("[data-sbm-collection-types]");
    if (types && types.labels && types.labels.length) {
      var counts = types.counts;
      this.typeScope = types.labels.filter(function (label, i) {
        return !counts || counts[i] > 0;
      });
      if (!this.typeScope.length) this.typeScope = null;
    }
  };

  SelectorController.prototype.strings = function () {
    return strings();
  };

  SelectorController.prototype.say = function (key, fallback, vars) {
    return say(key, fallback, vars);
  };

  SelectorController.prototype.readJson = function (selector) {
    var el = this.root.querySelector(selector);
    if (!el) return null;
    try {
      var data = JSON.parse(el.textContent);
      return data && data.labels && data.labels.length ? data : null;
    } catch (e) {
      /* offering the full catalogue beats offering nothing */
      return null;
    }
  };

  /**
   * Turns the flat "MAKE MODEL" facet labels into the same make/model shape as the catalogue
   * index, so the boxes can be filled from it. Needs the index first: the labels carry no
   * separator, so the make is recovered as the longest known make the label starts with.
   */
  SelectorController.prototype.buildScope = function () {
    if (!this.index) return;

    // The two scopes are independent: a collection usually has a complete make list and a
    // truncated machine list, so the make box can be narrowed where the model one cannot.
    if (this.scopeLabels) {
      var makes = Object.keys(this.index);
      var counts = this.scopeCounts;
      var baseOf = this.baseOf || {};
      var scope = {};

      this.scopeLabels.forEach(function (label, i) {
        if (label === UNIVERSAL) return;
        // Shopify keeps returning a facet value at zero once another filter has excluded it.
        if (counts && counts[i] === 0) return;

        var make = null;
        for (var j = 0; j < makes.length; j++) {
          if (label === makes[j] || label.indexOf(makes[j] + " ") === 0) {
            if (!make || makes[j].length > make.length) make = makes[j];
          }
        }
        if (!make) return;
        var model = label.slice(make.length).trim();
        if (!model) return;
        if (!scope[make]) scope[make] = {};
        scope[make][model] = true;
        // One spelling in this collection is enough to offer the plain name. The filter asks
        // for every spelling and Shopify returns whichever of them are here.
        var base = baseOf[make] && baseOf[make][model];
        if (base) scope[make][base] = true;
      });

      // Nothing resolved means the labels and the index disagree, not that the collection is
      // empty - fall back to the full catalogue rather than show empty boxes.
      this.scope = Object.keys(scope).length ? scope : null;
    }

    // Make facet labels are plain make names, so they only need checking against the index.
    if (this.makeScopeLabels) {
      var makeScope = {};
      var self = this;
      this.makeScopeLabels.forEach(function (label, i) {
        if (self.makeScopeCounts && self.makeScopeCounts[i] === 0) return;
        if (self.index[label]) makeScope[label] = true;
      });
      this.makeScope = Object.keys(makeScope).length ? makeScope : null;
    }
  };

  /**
   * The file is nested by vehicle type, because the finder asks for the type first. This folds
   * the types back into one make -> model tree - the shape the rest of this file works in.
   * Machines the catalogue publishes no type for sit under an empty key and are folded in with
   * the rest; they are real machines and belong here.
   *
   * The old flat file is still accepted, so a shopper holding a cached copy is not left with an
   * empty make list until it expires.
   */
  function flattenIndex(index, keepTypes, strict) {
    if (!index || !index.types) return index;
    var flat = {};
    Object.keys(index.types).forEach(function (type) {
      /**
       * Narrowed to the collection's own vehicle types when it said what they are. Machines the
       * catalogue publishes no type for are kept either way: they are real machines, and a
       * collection that stocks a part for one has no type facet value to declare it with, so
       * dropping them would hide a machine on the strength of a gap in the data.
       *
       * Not when the shopper picked the type themselves (strict). Then the untyped machines are
       * exactly what they asked not to see - 8,000 of them across 77 makes, so "ATV" offered
       * Ducati, Vespa and Harley-Davidson. "All types" is still the way to reach them.
       */
      if (keepTypes && (type || strict) && keepTypes.indexOf(type) === -1) return;
      var tree = index.types[type];
      Object.keys(tree).forEach(function (make) {
        var into = (flat[make] = flat[make] || {});
        Object.keys(tree[make]).forEach(function (model) {
          into[model] = into[model] ? F.mergeRanges(into[model].concat(tree[make][model])) : tree[make][model];
        });
      });
    });
    return flat;
  }

  /**
   * Whether a machine can be the shopper's year.
   *
   * A machine the catalogue publishes no years for passes every year rather than none. Outdoor
   * equipment is not sold by model year at all, and a gap in the data is not the same as a
   * machine that was not built that year - dropping those would hide real machines on the
   * strength of something nobody ever wrote down.
   */
  function yearFits(ranges, year) {
    if (!year) return true;
    if (!ranges || !ranges.length) return true;
    for (var i = 0; i < ranges.length; i++) {
      if (year >= ranges[i][0] && year <= ranges[i][1]) return true;
    }
    return false;
  }

  SelectorController.prototype.load = function () {
    if (this.index) return Promise.resolve(this.index);
    if (this.loading) return this.loading;
    var self = this;
    var url = this.root.getAttribute("data-machines-url");
    var loading = this.say("loading_machines", "Loading machines...");
    this.setStatus(loading);
    // The first box is live but empty until the index lands, so it says why. (With a Type box
    // first, that one is a plain dropdown and the status line below says it.)
    if (!this.typeEl && this.yearCombo.ok()) this.yearCombo.input.placeholder = loading;
    this.loading = fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error("machine index unavailable");
        return r.json();
      })
      .then(function (index) {
        self.groups = (index && index.groups) || {};
        // Makes whose products carry the machine's name rather than its spellings; see expand.
        self.curated = (index && index.curated) || [];
        self.rawIndex = index;
        self.index = flattenIndex(index, self.typeScope);
        self.applyGroups();
        self.fillTypes();
        self.fillYears();
        self.fillMakes();
        self.refreshSteps();
        self.showChosen();
        self.setStatus("");
        return index;
      })
      .catch(function () {
        self.refreshSteps();
        self.setStatus(say("machine_list_failed", "Could not load the machine list. Please refresh and try again."));
      });
    return this.loading;
  };

  /**
   * Puts the plain machine names into the tree beside the spellings they cover.
   *
   * Gamma files parts under the full trim name - SPORTSMAN 850 SP, 850 PREMIUM, 850 XP HO EPS
   * and 28 more - and a shopper knows only that they own a Sportsman 850. So the base name
   * becomes a machine the boxes can offer, carrying the years of every trim under it, and
   * `expand` turns it back into the exact spellings when the filter is built. Nothing about the
   * stored fitment changes; this is a name pointing at what the catalogue already published.
   *
   * A base that Gamma also publishes on its own is overwritten deliberately: picking it now
   * means the whole machine, so its years are the union rather than the bare listing's.
   */
  SelectorController.prototype.applyGroups = function () {
    this.baseOf = {};
    if (!this.index || !this.groups) return;
    var self = this;
    Object.keys(this.groups).forEach(function (make) {
      if (!self.index[make]) return;
      var bases = self.groups[make];
      self.baseOf[make] = {};
      Object.keys(bases).forEach(function (base) {
        var members = bases[base];
        var ranges = [];
        var known = 0;
        members.forEach(function (model) {
          self.baseOf[make][model] = base;
          if (self.index[make][model]) {
            known++;
            ranges = ranges.concat(self.index[make][model]);
          }
        });
        if (known) self.index[make][base] = F.mergeRanges(ranges);
      });
    });
  };

  /**
   * The exact machine names to filter on for a chosen one: every spelling when a base was
   * picked, and the machine itself otherwise. Shopify ORs repeated values of one filter, which
   * is how the UNIVERSAL sentinel already rides along beside a machine.
   */
  SelectorController.prototype.expand = function (make, model) {
    // A curated make's products carry the machine's name itself, so the name is the filter:
    // one value where a plain name used to send every spelling. A spelling picked by its trim
    // resolves to the name it belongs to; one nobody has placed is filtered on as it is.
    if (this.curated && this.curated.indexOf(make) !== -1) {
      var named = this.baseOf && this.baseOf[make] && this.baseOf[make][model];
      return [make + " " + (named || model)];
    }
    var group = this.groups && this.groups[make] && this.groups[make][model];
    if (!group) return [make + " " + model];
    return group.map(function (member) {
      return make + " " + member;
    });
  };

  SelectorController.prototype.setStatus = function (text) {
    if (this.statusEl) this.statusEl.textContent = text;
  };

  /**
   * Fills a <select>. `labels` is optional: when it holds a word for a value, that word is what
   * the shopper reads while the value submitted stays the one the fitment data uses. The Type
   * dropdown needs it - a part is tagged "SNOWMOBILE" in every language, and a French shopper
   * should still read "Motoneige".
   */
  SelectorController.prototype.fill = function (select, values, placeholder, labels) {
    select.innerHTML = "";
    var blank = document.createElement("option");
    blank.value = "";
    blank.textContent = placeholder;
    select.appendChild(blank);
    values.forEach(function (v) {
      var opt = document.createElement("option");
      var value = String(v);
      opt.value = value;
      opt.textContent = (labels && labels[value]) || value;
      select.appendChild(opt);
    });
    select.disabled = values.length === 0;
  };

  /**
   * The vehicle types the dropdown offers, off collections where nothing else narrows the list.
   * "All types" first as the way to skip it, then the types alphabetically.
   *
   * Held to the seven Gamma and Kimpex publish - see OFFERED_TYPES. If the file ever comes back
   * with none of them, every type in it is offered rather than an empty dropdown.
   */
  SelectorController.prototype.fillTypes = function () {
    if (!this.typeEl || !this.rawIndex || !this.rawIndex.types) return;
    var chosen = this.typeEl.value;
    var present = Object.keys(this.rawIndex.types).filter(Boolean);
    var offered = present.filter(function (type) {
      return OFFERED_TYPES.indexOf(type) !== -1;
    });
    var strings = this.strings();
    var labels = Object.assign({}, strings.types || {});
    labels[ALL_TYPES] = strings.all_types || "All types";
    var types = (offered.length ? offered : present).sort(function (a, b) {
      /* alphabetical in the language the shopper is reading, not in the data's */
      return String(labels[a] || a).localeCompare(String(labels[b] || b));
    });
    this.fill(this.typeEl, [ALL_TYPES].concat(types), strings.choose_type || "Select a type", labels);
    // Refilled under a shopper who had already picked one; the choice stays.
    if (chosen && this.hasTypeOption(chosen)) this.typeEl.value = chosen;
    this.typeEl.disabled = false;
  };

  /**
   * After a choice, moves on to the step now waiting for an answer - its list opens as it takes
   * focus - or to the button once every step is answered.
   */
  SelectorController.prototype.advance = function () {
    var field = this.root.querySelector("[data-sbm-panel='machine'] .sbm-field.is-current");
    var target = field ? field.querySelector("input:not([disabled]), select:not([disabled])") : this.submitEl;
    if (target && !target.disabled) target.focus();
  };

  /** Whether the type step is answered: a type, or "All types". Always, where there is no Type box. */
  SelectorController.prototype.typeAnswered = function () {
    return !this.typeEl || this.typeEl.value !== "";
  };

  /**
   * Opens each box once the one before it is answered - type, year, make, model - and marks
   * which step the shopper is on. Run after anything that changes an answer.
   *
   * The year counts as answered when the machines in scope publish no years at all: there is
   * nothing to ask, so the make opens straight away.
   */
  SelectorController.prototype.refreshSteps = function () {
    var ready = Boolean(this.index);
    var typeDone = this.typeAnswered();
    var yearDone = typeDone && ready && (this.yearEl.value !== "" || (this.yearCombo.loaded && !this.yearCombo.items.length));
    var makeDone = yearDone && this.makeEl.value !== "";
    var modelDone = makeDone && this.modelEl.value !== "";

    this.yearCombo.gate(typeDone, this.say("pick_type_first", "Select a type first"));
    this.makeCombo.gate(yearDone, this.say("pick_year_first", "Select a year first"));
    this.modelCombo.gate(makeDone, this.say("pick_make_first", "Select a make first"));

    var steps = [
      [this.typeEl && this.typeEl.closest(".sbm-field"), typeDone],
      [this.yearCombo.field, yearDone],
      [this.makeCombo.field, makeDone],
      [this.modelCombo.field, modelDone]
    ];
    var current = false;
    steps.forEach(function (step) {
      var field = step[0];
      if (!field) return;
      var isCurrent = !step[1] && !current;
      if (isCurrent) current = true;
      field.classList.toggle("is-done", step[1]);
      field.classList.toggle("is-current", isCurrent);
    });
  };

  /** Whether the Type box actually offers this one - see OFFERED_TYPES. */
  SelectorController.prototype.hasTypeOption = function (type) {
    if (!this.typeEl || !type) return false;
    var options = this.typeEl.options;
    for (var i = 0; i < options.length; i++) {
      if (options[i].value === type) return true;
    }
    return false;
  };

  /**
   * Narrows every box below to one vehicle type, or opens them back up.
   *
   * What was already answered below stays wherever the new type still offers it, so going back
   * to change the type does not undo the rest; only an answer the new lists no longer hold is
   * cleared. The same goes for the year and the make below.
   */
  SelectorController.prototype.onType = function () {
    var type = this.typeEl ? this.typeEl.value : "";
    this.typeScope = type && type !== ALL_TYPES ? [type] : null;
    this.index = flattenIndex(this.rawIndex, this.typeScope, true);
    this.applyGroups();
    var year = this.yearEl.value;
    this.fillYears();
    if (year && this.yearCombo.items.indexOf(year) !== -1) this.yearCombo.set(year);
    else this.yearCombo.clear();
    this.onYear();
  };

  /** Every machine in scope, as make, model and its year ranges, whatever narrowed it. */
  SelectorController.prototype.eachMachine = function (visit) {
    var index = this.index || {};
    var scope = this.scope;
    if (scope) {
      Object.keys(scope).forEach(function (make) {
        var known = index[make] || {};
        Object.keys(scope[make]).forEach(function (model) {
          visit(make, model, known[model]);
        });
      });
      return;
    }
    var makeScope = this.makeScope;
    Object.keys(index).forEach(function (make) {
      if (makeScope && !makeScope[make]) return;
      Object.keys(index[make]).forEach(function (model) {
        visit(make, model, index[make][model]);
      });
    });
  };

  /**
   * Every model year a machine in scope was built in, newest first, under "Any year".
   *
   * Asked before the make, so it is the whole catalogue's worth of years - which is fine,
   * because a year is a word the shopper already knows and types in four keystrokes.
   *
   * "Any year" is how the year is skipped. It is chosen like a year, and it is what opens the
   * make, but it narrows nothing: parsed as a number it is no year at all (see chosenYear).
   */
  SelectorController.prototype.fillYears = function () {
    this.buildScope();
    var seen = {};
    this.eachMachine(function (make, model, ranges) {
      if (!ranges) return;
      ranges.forEach(function (r) {
        for (var y = r[0]; y <= r[1]; y++) seen[y] = true;
      });
    });
    var years = Object.keys(seen)
      .map(Number)
      .sort(function (a, b) {
        return b - a;
      })
      .map(String);
    this.yearCombo.setItems(
      years.length ? [this.say("any_year", "Any year")].concat(years) : [],
      years.length ? this.say("choose_year", "Select a year") : this.say("not_sold_by_year", "Not sold by year")
    );
  };

  /** The year chosen, or null for "Any year" and for none: a year is only ever digits. */
  SelectorController.prototype.chosenYear = function () {
    var year = this.yearEl.value;
    return /^\d{4}$/.test(year) ? year : null;
  };

  SelectorController.prototype.onYear = function () {
    var make = this.makeEl.value;
    this.fillMakes();
    if (make && this.makeCombo.items.indexOf(make) !== -1) this.makeCombo.set(make);
    else this.makeCombo.clear();
    this.onMake(true);
  };

  /** The makes with at least one machine left after the type and the year. */
  SelectorController.prototype.fillMakes = function () {
    this.buildScope();
    var year = parseInt(this.yearEl.value || "", 10) || null;
    var seen = {};
    this.eachMachine(function (make, model, ranges) {
      if (yearFits(ranges, year)) seen[make] = true;
    });
    var makes = Object.keys(seen).sort();
    this.makeCombo.setItems(
      makes,
      makes.length
        ? this.say("search_makes_count", "Type to search {{ count }} makes", { count: makes.length })
        : this.say("no_makes_for_year", "No makes for that year")
    );
  };

  /**
   * Holds the models for one make, with the trim spellings folded under the plain name.
   *
   * The catalogue spells a machine many ways - Polaris alone runs to 5,595 entries, and the list
   * a shopper scrolls should be machines, not spellings. A trim whose plain name is also in the
   * list waits behind it and comes back the moment they type the trim, so nothing is unreachable:
   * SPORTSMAN 850 is what they see, SP is what they type if they know they own one.
   */
  SelectorController.prototype.setModels = function (models) {
    this.models = models || [];
    this.trimOf = {};

    var make = this.makeEl.value;
    var bases = (this.groups && this.groups[make]) || {};
    var inList = {};
    this.models.forEach(function (m) {
      inList[m] = true;
    });
    var trimOf = this.trimOf;
    Object.keys(bases).forEach(function (base) {
      if (!inList[base]) return;
      bases[base].forEach(function (member) {
        if (member !== base && inList[member]) {
          trimOf[member] = {
            trim: F.readableTrim(F.trimAgainst(base, member)).toUpperCase(),
            base: base
          };
        }
      });
    });

    this.visibleModels = this.models.filter(function (m) {
      return trimOf[m] === undefined;
    });

    var placeholder;
    if (this.visibleModels.length) {
      placeholder = this.say("search_models_count", "Type to search {{ count }} machines", {
        count: this.visibleModels.length
      });
    } else if (make) {
      placeholder = this.say("no_machines_for_year", "No machines for that year");
    } else {
      placeholder = this.say("pick_make_first", "Select a make first");
    }
    this.modelCombo.setItems(this.models, placeholder);
  };

  /** `keepModel`: a new type or year above keeps the model where it is still on the list. */
  SelectorController.prototype.onMake = function (keepModel) {
    var make = this.makeEl.value;
    var model = keepModel ? this.modelEl.value : "";
    this.modelCombo.clear();

    /**
     * Only the machine facet can scope the model list, and only when it came back complete.
     *
     * Narrowing the collection to one make first was tried and does not work: Shopify truncates
     * a facet to 100 values against the collection and does not recompute that against other
     * applied filters. Asking ATV/UTV Brake Pads for make=ADLY returns one product and the same
     * 100 machine values, not one of them an Adly. Reading a make's models out of that would be
     * reading them out of a list Adly never made it into - and worse for a make that is in it,
     * since its share of the 100 looks complete while being anything but.
     */
    var year = parseInt(this.yearEl.value || "", 10) || null;
    var index = this.index || {};
    var known = index[make] || {};
    var pool = [];
    if (make) {
      // Sorted here rather than trusted from the file: the plain names are added to the tree on
      // load, so a machine Gamma only ever spells with a trim would otherwise sit at the end.
      var names = this.scope ? Object.keys(this.scope[make] || {}) : Object.keys(known);
      pool = names
        .filter(function (model) {
          return yearFits(known[model], year);
        })
        .sort();
    }
    this.setModels(pool);
    if (model && this.models.indexOf(model) !== -1) this.modelCombo.set(model);
    this.refreshSubmit();
  };

  /**
   * The model list, ranked. A folded trim comes back two ways: they typed the trim itself
   * ("SP", "48"), or they typed past the machine name and are clearly after a particular one
   * ("SPORTSMAN 850 SP"). Typing only the machine name keeps the trims folded, which is the
   * point of folding them.
   */
  SelectorController.prototype.modelMatches = function (typed) {
    // Nothing typed: machines only. Typing searches every spelling, a folded trim among them
    // once what they typed is in the trim itself.
    var models = typed ? this.models || [] : this.visibleModels || this.models || [];
    var trimOf = this.trimOf || {};
    var starts = [];
    var contains = [];
    /**
     * With nothing typed, machines with a name come before the ones the catalogue names by
     * engine size alone ("250", "400L"): sorted plainly, the digits win and the first screen of
     * a make is nothing but numbers. The list is longer here too - 100 rather than 25 - since a
     * shopper who has not typed is browsing, not narrowing.
     */
    if (!typed) {
      models = models.slice().sort(function (a, b) {
        var an = /^\d/.test(a) ? 1 : 0;
        var bn = /^\d/.test(b) ? 1 : 0;
        return an - bn || a.localeCompare(b);
      });
    }
    var cap = typed ? 25 : 100;

    for (var i = 0; i < models.length; i++) {
      var folded = trimOf[models[i]];
      if (folded) {
        var byTrim = folded.trim.indexOf(typed) !== -1;
        var beyondMachine = folded.base.indexOf(typed) === -1 && models[i].indexOf(typed) !== -1;
        if (!typed || (!byTrim && !beyondMachine)) continue;
      }
      if (!typed) {
        starts.push(models[i]);
      } else if (models[i].indexOf(typed) === 0) {
        starts.push(models[i]);
      } else if (models[i].indexOf(typed) !== -1) {
        contains.push(models[i]);
      }
      if (starts.length >= cap) break;
    }
    return starts.concat(contains).slice(0, cap);
  };

  SelectorController.prototype.onModel = function () {
    this.refreshSubmit();
  };

  SelectorController.prototype.refreshSubmit = function () {
    this.submitEl.disabled = !(this.makeEl.value && this.modelEl.value);
    this.refreshSteps();
    this.refreshReset();
    this.clearOffer();
  };

  /** Half-typed text counts as something to clear, not just a settled choice. */
  SelectorController.prototype.refreshReset = function () {
    if (!this.resetEl) return;
    var typed =
      (this.yearCombo.ok() && this.yearCombo.input.value) ||
      (this.makeCombo.ok() && this.makeCombo.input.value) ||
      (this.modelCombo.ok() && this.modelCombo.input.value);
    var type = this.typeEl && this.typeEl.value;
    this.resetEl.hidden = !(type || this.makeEl.value || this.yearEl.value || typed);
  };

  /**
   * Empties the controls without reloading. The remembered machine goes too: leaving it behind
   * would refill these same controls on the next collection, which would read as the clear not
   * having worked. Any filter already applied to the page is left alone - undoing that is what
   * "Show all parts" is for, and it is still on screen to do it.
   */
  SelectorController.prototype.reset = function () {
    clearGarage();
    this.showChosen();
    // Emptied first: a new type or year keeps whatever below it still fits, and here nothing
    // is meant to.
    this.yearCombo.clear();
    this.makeCombo.clear();
    this.modelCombo.clear();
    // Back to the first box, not the second: the type is a choice like any other, and leaving
    // it set means "clear" cleared everything except the one thing still narrowing the lists.
    if (this.typeEl) {
      this.typeEl.value = "";
      this.onType();
      this.typeEl.focus();
    } else {
      this.onYear();
      if (this.yearCombo.ok()) this.yearCombo.input.focus();
    }
    this.setStatus("");
  };

  /**
   * The "parts for your machine" page (machine-results.js on the search route). It takes the
   * machine's name and year and draws the whole list itself, so no filter values travel here.
   */
  SelectorController.prototype.searchUrlFor = function (machine) {
    var params = new URLSearchParams();
    var named = this.baseOf && this.baseOf[machine.make] && this.baseOf[machine.make][machine.model];
    params.set(MACHINE_PARAM, machine.make + " " + (named || machine.model));
    if (machine.year) params.set(YEAR_PARAM, machine.year);
    if (this.universalEl && this.universalEl.checked) params.set("fit_universal", "1");
    return (this.root.getAttribute("data-search-url") || "/search") + "?" + params.toString();
  };

  SelectorController.prototype.filterParams = function (machine) {
    var params = new URLSearchParams(window.location.search);
    // Rebuild from scratch: leaving a previous machine on would AND the two together and
    // return nothing, since no part is filtered to two machines at once.
    params.delete(FILTER_KEY);
    params.delete(YEAR_PARAM);
    params.delete("page");
    params.delete(MACHINE_PARAM);
    var spellings = this.expand(machine.make, machine.model);
    spellings.forEach(function (label) {
      params.append(FILTER_KEY, label);
    });
    if (spellings.length > 1) params.set(MACHINE_PARAM, machine.make + " " + machine.model);
    if (this.universalEl && this.universalEl.checked) params.append(FILTER_KEY, UNIVERSAL);
    if (machine.year) params.set(YEAR_PARAM, machine.year);
    return params;
  };

  SelectorController.prototype.apply = function () {
    var make = this.makeEl.value;
    var model = this.modelEl.value;
    if (!make || !model) return;
    /**
     * The vehicle type rides along with the machine so the boxes come back the way they were
     * left. It narrows nothing about the answer - the filter is the machine itself - but a Type
     * that says "All types" beside a Sportsman reads as the page having forgotten half of what
     * was asked.
     *
     * Where there is no Type box at all, on a collection, whatever was chosen last is carried
     * forward rather than wiped by a page that never offered the choice.
     */
    var remembered = readGarage() || {};
    var machine = {
      make: make,
      model: model,
      year: this.chosenYear(),
      type: (this.typeEl ? this.typeEl.value.replace(ALL_TYPES, "") : remembered.type) || "",
      // Kept apart from `type` above deliberately: that one is the filter the shopper set and
      // is put back into the Type box, this one is what the machine actually is and is only
      // ever shown. Resolved now, while the index is in hand, so the label needs nothing later.
      machineType: this.typeOfMachine(make, model)
    };
    writeGarage(machine);
    announceGarage();
    this.showChosen();
    /**
     * Folded from here on. Opening the box is how a shopper asks the question, and pressing this
     * button is the question being answered - so the next page, and every page after it, gets
     * the one-line version naming their machine rather than the row of boxes they have finished
     * with. Opening it again is one click, and that click is remembered the same way.
     */
    writeOpen(false);

    /**
     * Opened from a product page's fit box to ask "does this part fit?" - so the answer is on
     * that page, and sending the shopper off to a list of parts would lose the part they were
     * looking at. The box folds away and the fit box answers. Pressed anywhere else, the button
     * still does what it says.
     */
    if (this.root.hasAttribute("data-sbm-stay")) {
      this.setOpen(false, false);
      return;
    }

    /**
     * Shopify serves no storefront filters at all on collections past roughly 5,000 products,
     * so on those pages the filter parameter would be silently ignored. Search honours the same
     * filter, so the shopper is sent there instead and gets every part in the catalogue that
     * fits - a broader answer than they asked for, but a real one rather than no change.
     */
    if (this.root.getAttribute("data-filter-available") === "false") {
      // The next page is a fetch away, so the button says it heard the press rather than
      // sitting there looking unpressed until that page paints.
      this.setBusy(true, say("finding_parts", "Finding the parts that fit..."));
      window.location.href = this.searchUrlFor(machine);
      return;
    }

    var params = this.filterParams(machine);

    /**
     * The boxes cannot always be narrowed to the collection - Shopify caps a facet at 100
     * values and gives no sign that it truncated, so on most collections the model list is the
     * whole catalogue and a shopper can pick a machine this category holds nothing for. Rather
     * than send them to an empty grid, the grid is rendered on its own first and only navigated
     * to if it has products. When it has none they are told so and offered the whole store,
     * where the same filter does have answers.
     */
    var self = this;
    this.setBusy(true, say("checking_parts", "Checking for parts that fit..."));
    this.probe(params).then(function (doc) {
      // A check that could not complete must never block the shopper: go where they asked and
      // let the collection page speak for itself. The button stays busy through the navigation,
      // because the page is about to be replaced and an idle button in the meantime reads as
      // nothing having happened.
      if (!doc || doc.querySelector(PRODUCT_CARD)) {
        self.setStatus(say("loading_parts", "Loading the parts that fit..."));
        window.location.search = params.toString();
        return;
      }
      self.setBusy(false, "");
      self.offerSearch(machine);
    });
  };

  /** Re-renders just the collection grid for a set of filters, without leaving the page. */
  SelectorController.prototype.probe = function (params) {
    var url =
      window.location.pathname +
      "?" +
      params.toString() +
      "&section_id=" +
      encodeURIComponent(this.gridSectionId());

    var controller = typeof AbortController === "function" ? new AbortController() : null;
    var timer = controller
      ? window.setTimeout(function () {
          controller.abort();
        }, PROBE_TIMEOUT)
      : null;

    return fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (r) {
        if (!r.ok) throw new Error("probe failed");
        return r.text();
      })
      .then(function (html) {
        if (timer) window.clearTimeout(timer);
        return new DOMParser().parseFromString(html, "text/html");
      })
      .catch(function () {
        if (timer) window.clearTimeout(timer);
        return null;
      });
  };

  /**
   * The grid is a section, so it can be re-rendered on its own. Its id is read off a product
   * card on the page rather than hardcoded, since the three collection templates could drift;
   * an empty grid leaves no card to read, so Liquid passes the template's key as the fallback.
   */
  SelectorController.prototype.gridSectionId = function () {
    var card = document.querySelector("[data-product-id]");
    var host = card && card.closest("[id^='shopify-section-']");
    if (host) return host.id.replace("shopify-section-", "");
    return this.root.getAttribute("data-grid-section-id") || "product-grid";
  };

  /** The button says it heard the press: a spinner, its own words, and no second press. */
  SelectorController.prototype.setBusy = function (busy, message) {
    this.submitEl.disabled = busy || !(this.makeEl.value && this.modelEl.value);
    this.submitEl.classList.toggle("is-busy", Boolean(busy));
    this.submitEl.setAttribute("aria-busy", busy ? "true" : "false");
    this.setStatus(message);
  };

  SelectorController.prototype.offerSearch = function (machine) {
    this.offerShown = true;
    this.setStatus(
      say("no_parts_for_machine", "No parts in this category fit your {{ machine }}.", {
        machine: machine.make + " " + machine.model
      })
    );
    if (this.searchLinkEl) {
      this.searchLinkEl.href = this.searchUrlFor(machine);
      this.searchLinkEl.hidden = false;
    }
  };

  /** Drops a stale "nothing fits" offer as soon as the shopper changes what they are asking. */
  SelectorController.prototype.clearOffer = function () {
    if (!this.offerShown) return;
    this.offerShown = false;
    this.setStatus("");
    if (this.searchLinkEl) this.searchLinkEl.hidden = true;
  };

  SelectorController.prototype.clear = function () {
    clearGarage();
    var params = new URLSearchParams(window.location.search);
    params.delete(FILTER_KEY);
    params.delete(YEAR_PARAM);
    params.delete(MACHINE_PARAM);
    params.delete("page");
    window.location.search = params.toString();
  };

  /** Puts the saved machine back into the boxes so it carries across collections. */
  SelectorController.prototype.restore = function () {
    var params = new URLSearchParams(window.location.search);
    var activeAll = params.getAll(FILTER_KEY).filter(function (v) {
      return v !== UNIVERSAL;
    });
    var active = activeAll[0];
    var garage = readGarage();
    if (!active && !garage) return;

    var self = this;
    this.load().then(function () {
      if (!self.index) return;
      var make = garage && garage.make;
      var model = garage && garage.model;
      var year = garage && garage.year;

      if (active) {
        // The filter value is "MAKE MODEL" with no separator, so the make is recovered by
        // finding the known make it starts with rather than by splitting on a space.
        Object.keys(self.index).forEach(function (candidate) {
          if (active === candidate || active.indexOf(candidate + " ") === 0) {
            if (!make || candidate.length > make.length) {
              make = candidate;
              model = active.slice(candidate.length).trim();
            }
          }
        });
        year = params.get(YEAR_PARAM) || year;

        /**
         * A plain machine name filters on every spelling of it, so what comes back in the URL is
         * the spellings, not the name the shopper picked. Put the name back when the whole group
         * is there - restoring one spelling would quietly narrow their next search to one trim.
         */
        var base = make && self.baseOf && self.baseOf[make] && self.baseOf[make][model];
        if (base) {
          var members = self.groups[make][base];
          var whole = members.every(function (member) {
            return activeAll.indexOf(make + " " + member) !== -1;
          });
          if (whole) model = base;
        }
      }

      if (!make || !self.index[make]) return;

      var machineName = model ? make + " " + model : make;

      // The remembered machine may have nothing in this collection, in which case it is not in
      // the boxes at all. Saying so beats leaving the controls blank as though the garage had
      // been forgotten.
      if (self.scope && !self.scope[make]) {
        self.setStatus(
          say("nothing_fits", "Nothing in this category fits your {{ machine }}.", { machine: machineName })
        );
        return;
      }

      /**
       * The type goes back before anything else, because it decides which machines the boxes
       * below are filled from. Dropped when the remembered machine is not one of that type's -
       * a stale type would empty the boxes rather than fill them.
       */
      /**
       * The Type box says what kind of machine theirs is, not merely what they last filtered on.
       * A shopper who left it on "All types" and picked an ACE 500 was shown "All types" beside
       * a machine the catalogue files under ATV, which reads as the box having lost half of what
       * it was told. So the machine's own type stands in where no type was chosen.
       */
      var type = garage && garage.type;
      if (!type) type = (garage && garage.machineType) || self.typeOfMachine(make, model);
      if (
        type &&
        self.hasTypeOption(type) &&
        self.rawIndex &&
        self.rawIndex.types &&
        self.rawIndex.types[type]
      ) {
        var wholeIndex = self.index;
        var wholeScope = self.typeScope;
        self.typeScope = [type];
        self.index = flattenIndex(self.rawIndex, self.typeScope, true);
        self.applyGroups();
        // The machine has to survive the narrowing, or the narrowing is put back: an empty set
        // of boxes is a worse answer than a Type that has been forgotten.
        var survives = self.index[make] && (!model || self.index[make][model]);
        if (survives) {
          self.typeEl.value = type;
          self.fillYears();
        } else {
          self.typeScope = wholeScope;
          self.index = wholeIndex;
          self.applyGroups();
        }
      }
      // A machine of a type the box does not offer is still their machine, found under "All
      // types" - which is what the box then says, so the steps below it read as answered.
      if (self.typeEl && !self.typeEl.value) self.typeEl.value = ALL_TYPES;

      // The year goes back next, because it decides which makes and models are offered below.
      // Saved without one, it was "Any year".
      if (year) {
        self.yearCombo.set(String(year));
        self.fillMakes();
      } else if (self.yearCombo.items.length) {
        self.yearCombo.set(self.yearCombo.items[0]);
      }

      self.makeCombo.set(make);
      self.onMake();

      var modelAvailable = false;
      if (model) {
        modelAvailable = self.scope
          ? Boolean(self.scope[make][model])
          : Boolean(self.index[make][model]);
      }

      if (modelAvailable) {
        self.modelCombo.set(model);
      } else if (model) {
        self.setStatus(
          say("nothing_fits", "Nothing in this category fits your {{ machine }}.", { machine: machineName })
        );
      }
      self.refreshSubmit();
      if (garage && !active) self.root.setAttribute("data-sbm-suggest", "1");
    });
  };

  SelectorController.prototype.bind = function () {
    var self = this;

    /**
     * The index is fetched on first contact with the section rather than on page load, since
     * 1.5 MB is not worth spending on every shopper who scrolls past. Listening on the section
     * itself, not on the controls, is what makes that safe: pointing at or tabbing into the
     * section starts the fetch well before the shopper reaches the boxes, and it works even
     * while the boxes further down the row are still empty.
     */
    ["pointerenter", "pointerdown", "touchstart", "focusin"].forEach(function (evt) {
      self.root.addEventListener(
        evt,
        function () {
          self.load();
        },
        { once: true, passive: true }
      );
    });

    if (this.toggleEl) {
      this.toggleEl.addEventListener("click", function () {
        self.setOpen(self.bodyEl.hidden, true);
      });
    }
    if (this.typeEl) {
      this.typeEl.addEventListener("change", function () {
        self.onType();
        if (self.typeEl.value) self.advance();
      });
    }
    this.submitEl.addEventListener("click", function () {
      self.apply();
    });
    if (this.clearEl) {
      this.clearEl.addEventListener("click", function () {
        self.clear();
      });
    }
    if (this.resetEl) {
      this.resetEl.addEventListener("click", function () {
        self.reset();
      });
    }
  };

  /**
   * The tire half of the box.
   *
   * A shopper buying tires is answering a different question from a shopper buying parts. They
   * are not looking for what fits their machine - they already know their size, it is moulded
   * into the sidewall of the tire they are replacing - so asking them for a year, a make and a
   * model to arrive at it is three steps that tell them nothing they did not already know.
   *
   * Two controls, then. The type only decides which list of sizes is offered; it is the size
   * that finds the tires.
   */
  function TireFinder(root) {
    this.root = root;
    this.typeEl = root.querySelector("[data-sbm-tire-type]");
    this.submitEl = root.querySelector("[data-sbm-tire-submit]");
    this.statusEl = root.querySelector("[data-sbm-tire-status]");
    this.data = null;
    this.loading = null;

    /** The three boxes, in the order the numbers are printed on the sidewall. */
    this.levels = [];
    for (var i = 0; i < 3; i++) {
      this.levels.push({
        select: root.querySelector("[data-sbm-size='" + i + "']"),
        label: root.querySelector("[data-sbm-size-label='" + i + "']")
      });
    }
    this.bind();
  }

  /**
   * A size with only two numbers - 2.75-21 has no aspect ratio - still has to answer the middle
   * box, and "no aspect ratio" is a real answer rather than an unanswered box.
   *
   * The two have to be told apart or the boxes after it read an unanswered box as "the ones
   * with nothing there" and come back empty. So the empty part is carried as its own value and
   * only ever shown as a dash.
   */
  var NO_PART = "none";
  var NO_PART_TEXT = "—";

  /** The value a box carries for a part, keeping "" for "not answered yet". */
  function partValue(part) {
    return part === "" ? NO_PART : part;
  }

  TireFinder.prototype.ok = function () {
    if (!this.typeEl || !this.submitEl) return false;
    for (var i = 0; i < this.levels.length; i++) {
      if (!this.levels[i].select) return false;
    }
    return true;
  };

  TireFinder.prototype.setStatus = function (text) {
    if (this.statusEl) this.statusEl.textContent = text || "";
  };

  /** The sizes of the type currently chosen. */
  TireFinder.prototype.sizes = function () {
    var group = this.data && this.data.types && this.data.types[this.typeEl.value];
    return (group && group.sizes) || [];
  };

  /**
   * Fetched on the first switch to this tab rather than on page load. It is 43 KB, which is
   * nothing beside the 1.5 MB machine index, but a shopper who never asks about tires should
   * still not pay for it.
   */
  TireFinder.prototype.load = function () {
    if (this.data) return Promise.resolve(this.data);
    if (this.loading) return this.loading;
    if (!this.ok()) return Promise.resolve(null);
    var self = this;
    var url = this.root.getAttribute("data-tire-sizes-url");
    if (!url) return Promise.resolve(null);
    this.setStatus(say("loading_sizes", "Loading sizes..."));
    this.loading = fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error("tire sizes unavailable");
        return r.json();
      })
      .then(function (data) {
        self.data = data;
        self.restore();
        self.setStatus("");
        return data;
      })
      .catch(function () {
        self.setStatus(say("tire_sizes_failed", "Could not load the tire sizes. Please refresh and try again."));
      });
    return this.loading;
  };

  /**
   * Names the three boxes for the type chosen.
   *
   * A quad tire is a height by a width on a rim; a motorcycle one is a width, an aspect ratio
   * and a rim. The same three boxes ask a different question of each, and the file says which.
   */
  TireFinder.prototype.relabel = function () {
    var group = this.data && this.data.types && this.data.types[this.typeEl.value];
    var fields = (group && group.fields) || [];
    for (var i = 0; i < this.levels.length; i++) {
      if (this.levels[i].label && fields[i]) this.levels[i].label.textContent = fields[i];
    }
  };

  /**
   * The sizes still possible once the boxes before this one have been answered.
   *
   * A box left unanswered narrows nothing, so the boxes after it still offer everything. That
   * matters on the way back: changing the height empties the width, and the rim list has to
   * widen again rather than collapse to nothing.
   */
  TireFinder.prototype.narrowed = function (level) {
    var chosen = [];
    for (var i = 0; i < level; i++) chosen.push(this.levels[i].select.value);
    return this.sizes().filter(function (size) {
      for (var i = 0; i < chosen.length; i++) {
        if (!chosen[i]) continue;
        if (partValue(size.parts[i]) !== chosen[i]) return false;
      }
      return true;
    });
  };

  /**
   * Fills this box and every box after it.
   *
   * Each one only ever offers what the boxes before it leave possible, so a shopper cannot
   * assemble a size the store does not stock and land on an empty page. A box left with one
   * answer settles itself, which is how the middle box takes care of itself on an old inch
   * size: 2.75-21 has no aspect ratio, so there is nothing there to ask about.
   */
  TireFinder.prototype.fillFrom = function (level) {
    for (var i = level; i < this.levels.length; i++) {
      var select = this.levels[i].select;
      var possible = this.narrowed(i);
      var seen = {};
      var values = [];
      for (var j = 0; j < possible.length; j++) {
        var part = possible[j].parts[i];
        if (seen[part]) continue;
        seen[part] = true;
        values.push(part);
      }
      values.sort(function (a, b) {
        var x = parseFloat(a);
        var y = parseFloat(b);
        if (isFinite(x) && isFinite(y) && x !== y) return x - y;
        return String(a).localeCompare(String(b));
      });

      select.innerHTML = "";
      // The empty first line only where there is a choice to make. Above a single option it is
      // a step that exists to be clicked past.
      if (values.length !== 1) {
        select.appendChild(new Option(i === 0 ? "Choose..." : "Select", ""));
      }
      for (var k = 0; k < values.length; k++) {
        select.appendChild(new Option(values[k] === "" ? NO_PART_TEXT : values[k], partValue(values[k])));
      }
      select.disabled = values.length === 0;
      select.value = values.length === 1 ? partValue(values[0]) : "";
    }
    this.refreshSubmit();
  };

  /**
   * Puts back the size they last searched for.
   *
   * The same courtesy the garage does for their machine: somebody fitting a set of tires asks
   * about one size two or three times - a front, then a rear, then the same size in another
   * tread - and arriving at an empty row of boxes each time makes them do the work again.
   *
   * Restored through the boxes rather than around them, so the cascade still decides what is
   * offered: a remembered answer the catalogue no longer carries is simply not there to be
   * chosen, and the boxes after it are left open rather than pointing at a size that has gone.
   */
  TireFinder.prototype.restore = function () {
    var saved = readTire();
    if (saved && saved.type && this.data && this.data.types && this.data.types[saved.type]) {
      this.typeEl.value = saved.type;
    }
    this.relabel();
    this.fillFrom(0);

    var parts = (saved && saved.parts) || [];
    for (var i = 0; i < this.levels.length; i++) {
      var select = this.levels[i].select;
      var wanted = parts[i];
      if (!wanted) break;
      var found = false;
      for (var j = 0; j < select.options.length; j++) {
        if (select.options[j].value === wanted) found = true;
      }
      if (!found) break;
      select.value = wanted;
      this.fillFrom(i + 1);
    }
  };

  /**
   * The one size the three boxes name, or nothing while they still name several.
   *
   * Answering every box is not the test - narrowing to one size is. Where a height leaves only
   * one width and one rim, all three settle themselves and the button is ready before the
   * shopper has touched the last two.
   */
  TireFinder.prototype.chosen = function () {
    var possible = this.narrowed(this.levels.length);
    return possible.length === 1 ? possible[0] : null;
  };

  TireFinder.prototype.refreshSubmit = function () {
    if (!this.ok()) return;
    this.submitEl.disabled = !this.chosen();
  };

  /**
   * Where a size sends them.
   *
   * Every spelling of the chosen size goes into the URL at once, because Shopify reads repeated
   * values of one filter as "any of these". A shopper picking 25 by 12 on a 9 reaches the
   * listing spelt "25x12-9 Rear" and the one spelt "25X12-9" together, which is the whole
   * reason the size list is built from the store rather than from a supplier's file.
   *
   * The destination is the Tires collection rather than the ATV or motorcycle one. It holds
   * every tire in stock, so no missing tag can hide a listing, and the size does the narrowing
   * on its own: nothing on a motorcycle is made in an ATV size.
   */
  TireFinder.prototype.urlFor = function (size) {
    var values = size.values && size.values.length ? size.values : [size.label];
    var base = (this.root.getAttribute("data-collections-url") || "/collections").replace(/\/$/, "");
    var handle = (this.data && this.data.collection) || "pneus";
    var params = [];
    for (var i = 0; i < values.length; i++) {
      params.push("filter.v.option.size=" + encodeURIComponent(values[i]));
    }
    return base + "/" + handle + "?" + params.join("&");
  };

  TireFinder.prototype.apply = function () {
    if (!this.ok()) return;
    var size = this.chosen();
    if (!size) return;

    var parts = [];
    for (var i = 0; i < this.levels.length; i++) parts.push(this.levels[i].select.value);
    writeTire({ type: this.typeEl.value, parts: parts, label: size.label });

    // Folded from here on, for the reason the machine half folds: pressing the button is the
    // question being answered, so the next page gets the one line naming their size rather than
    // the row of boxes they have finished with.
    writeOpen(false);

    this.setStatus(say("finding_tires", "Finding {{ size }} tires...", { size: size.label }));
    window.location.href = this.urlFor(size);
  };

  TireFinder.prototype.bind = function () {
    if (!this.ok()) return;
    var self = this;
    this.typeEl.addEventListener("change", function () {
      // A motorcycle size means nothing in the ATV list, so the answers go with the list.
      self.relabel();
      self.fillFrom(0);
    });
    this.levels.forEach(function (level, i) {
      level.select.addEventListener("change", function () {
        self.fillFrom(i + 1);
      });
    });
    this.submitEl.addEventListener("click", function () {
      self.apply();
    });
  };

  /**
   * The two tabs.
   *
   * Which one a shopper last used is remembered, the same way the box remembers whether it was
   * left open: somebody fitting a set of tires asks the same question two or three times, and
   * being put back on the machine tab every page would make them click twice each time.
   */
  function bindModes(root, selector, tire) {
    var buttons = root.querySelectorAll("[data-sbm-mode]");
    if (!buttons.length) return;

    var panels = {};
    root.querySelectorAll("[data-sbm-panel]").forEach(function (panel) {
      panels[panel.getAttribute("data-sbm-panel")] = panel;
    });

    function show(mode) {
      buttons.forEach(function (button) {
        var on = button.getAttribute("data-sbm-mode") === mode;
        button.classList.toggle("is-active", on);
        button.setAttribute("aria-selected", on ? "true" : "false");
      });
      Object.keys(panels).forEach(function (name) {
        panels[name].hidden = name !== mode;
      });
      if (mode === "tire" && tire) tire.load();
      writeMode(mode);
      // The chip answers whichever tab is showing, so it is repainted after the mode is written.
      if (selector) selector.showChosen();
    }

    buttons.forEach(function (button) {
      button.addEventListener("click", function () {
        show(button.getAttribute("data-sbm-mode"));
      });
    });

    if (readMode() === "tire" && panels.tire) show("tire");
  }

  function init() {
    var boxes = document.querySelectorAll("[data-shop-by-machine]");
    boxes.forEach(function (root) {
      if (root.dataset.sbmReady) return;
      /**
       * The one in the header wins. It is the copy shoppers use - it is on every page and it
       * travels with the header when they scroll - so a second one dropped onto a page would
       * be the same words twice, with only one of them named in the header.
       */
      /**
       * Except on a page whose own content makes the finder its main event - the home page hero.
       * There the header's copy steps aside instead, the other way round.
       */
      if (root.hasAttribute("data-sbm-header") && document.querySelector("[data-sbm-primary]")) {
        root.hidden = true;
        return;
      }
      if (
        !root.hasAttribute("data-sbm-header") &&
        !root.hasAttribute("data-sbm-primary") &&
        document.querySelector("[data-sbm-header]")
      ) {
        root.hidden = true;
        return;
      }
      root.dataset.sbmReady = "1";
      var selector = new SelectorController(root);
      // Only where Liquid rendered the tire half; the setting can switch it off.
      var tire = root.querySelector("[data-sbm-panel='tire']") ? new TireFinder(root) : null;
      bindModes(root, selector, tire);
    });
    paintBadges();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Collection filtering swaps the grid without a page load, so badges are repainted then too.
  document.addEventListener("collection:updated", paintBadges);
  document.addEventListener("facets:updated", paintBadges);

  window.ShopByMachine = { readGarage: readGarage, paintBadges: paintBadges };
})();
